import { useCallback, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Flex } from '@chakra-ui/react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { serialize } from 'object-to-formdata';
import { concat, differenceBy } from 'lodash';
import HeaderNavigation from 'components/HeaderNavigation';
import { FORM_MAX_WIDTH } from 'utils/constants';
import FooterButtons, { FOOTER_HEIGHT } from 'components/FooterButtons';
import FormBody from 'pages/Dashboard/Form/components/FormBody';
import { BODY_SCHEMA, DEFAULT_VALUES } from 'pages/Dashboard/Form/constants';
import ROUTES from 'app/routes';
import { API, APIRoutes } from 'api';
import { setFormErrors } from 'utils/api';
import LoadingIndicator from 'components/LoadingIndicator';
import ComponentPreview from 'components/PreviewWrapper';
import { SERIALIZER_OPTIONS } from 'app/constants';
import { convertKeysToSnakeCase, transformPosition, transformRemoved } from 'utils/helpers';
import DndWrapper from 'components/DndWrapper';
import ContentComponentsForm from 'components/Content/ContentComponentsForm';
import AddButton from 'components/AddButton';
import { parseContentComponents } from 'components/Content/constants';
import { useGetDashboard } from 'api/dashboard';
import { prepareDashboardContainer } from 'pages/Dashboard/Containers/Form/constants';
import FormWrapper from 'components/FormWrapper';

function DashboardEdit() {
  const { dashboardId: id = '' } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  let debounce: ReturnType<typeof setTimeout>;

  const methods = useForm<Dashboard>({
    mode: 'onChange',
    resolver: yupResolver(BODY_SCHEMA),
    defaultValues: DEFAULT_VALUES,
  });

  const { handleSubmit, setError, reset, control } = methods;

  const {
    fields: containers,
    append,
    remove,
    move,
  } = useFieldArray({
    control,
    name: 'contentContainersAttributes',
    keyName: 'fieldId',
    shouldUnregister: true,
  });

  const addContainerHandler = useCallback(() => {
    append({ kind: '' } as ContentContainer);
  }, [append]);

  const reorderContainers = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      clearTimeout(debounce);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      debounce = setTimeout(() => move(dragIndex, hoverIndex), 500);
    },
    [move],
  );

  const { data: { data: dashboard } = {}, isFetching } = useGetDashboard(+id);

  const { mutate: editDashboard, isLoading: isEditing } = useMutation({
    mutationKey: 'dashboardContainersMutation',
    mutationFn: (data: Dashboard) => {
      const removedContainers = differenceBy<ContentContainer, ContentContainer>(
        dashboard?.contentContainers,
        data?.contentContainersAttributes,
        'id',
      );

      data.contentContainersAttributes = data?.contentContainersAttributes?.map(prepareDashboardContainer);

      data.contentContainersAttributes = concat<ContentContainer>(
        ...transformPosition(data?.contentContainersAttributes),
        ...transformRemoved(removedContainers),
      );

      const snakePayload = convertKeysToSnakeCase(data);
      const formData = serialize(snakePayload, SERIALIZER_OPTIONS, new FormData(), 'dashboard');
      return API.put(APIRoutes.dashboard.byId(+id), formData);
    },
    onSuccess: () => {
      toast.success('Zmiany w dashboardzie zostały zastosowane');
      navigate(ROUTES.dashboard.base);
      void queryClient.invalidateQueries('dashboardContainers');
    },
    onError: ({ errors }) => {
      setFormErrors(setError, errors);
      toast.error('Wystąpił problem podczas edycji dashboardu');
    },
  });

  const onSubmit = handleSubmit((data: Dashboard) => editDashboard(data));

  useEffect(() => {
    if (dashboard) {
      reset({ ...DEFAULT_VALUES, ...parseContentComponents(dashboard) });
    }
  }, [dashboard, reset]);

  const isLoading = isFetching || isEditing;

  return (
    <Box>
      <HeaderNavigation
        baseCrumb={{
          label: 'Dashboard',
          to: ROUTES.dashboard.base,
        }}
        crumbs={[{ to: '', label: 'Edytuj dashboard' }]}
      />
      {isFetching && <LoadingIndicator />}
      <FormWrapper {...methods}>
        <Flex gap={4} mb={4} alignItems="flex-start" pb={FOOTER_HEIGHT}>
          <Box w="100%" as="form" id="dashboard-editor" onSubmit={onSubmit} maxW={FORM_MAX_WIDTH}>
            <FormBody isLoading={isLoading} />
            {containers.map((container, index) => (
              <DndWrapper
                key={`dashboard-editor-${container.fieldId}`}
                id={container.fieldId}
                isLoading={isLoading}
                index={index}
                reorderContainers={reorderContainers}
                removeContainer={remove}
                ContainerWrapper={ContentComponentsForm}
                multipleContainers
                isDashboard
              />
            ))}
            <AddButton addHandler={addContainerHandler} mt={2} />
          </Box>
          <ComponentPreview kind="dashboard" isEditMode />
        </Flex>
      </FormWrapper>
      <FooterButtons isLoading={isLoading} formId="dashboard-editor" onCancel={() => navigate(-2)} />
    </Box>
  );
}

export default DashboardEdit;
